//#region types
import { UploadedFile } from "models/utilTypes/fileUtilsTypes";
import { WintSuperkollAPIModelQuestionsAndAnswers, WintSuperkollAPIModelSurveys } from "wint-api-client/dist/types";
import { Question, QuestionGroup, Answer } from "models/propTypes/genericFormProps";
//#endregion
import { FormHelper } from "utils/helpers";

function useDynamicFormInitialValues(
  questionGroups: QuestionGroup[],
  answers: Answer[],
  groupAnswers?: WintSuperkollAPIModelSurveys.GroupAnswerModel[]
) {
  const getValue = (answer: Answer, question: Question) => {
    switch (answer.kind) {
      case WintSuperkollAPIModelQuestionsAndAnswers.AnswerKind.Boolean:
        return answer.booleanValue === undefined ? undefined : answer.booleanValue ? 1 : 0;
      case WintSuperkollAPIModelQuestionsAndAnswers.AnswerKind.Text:
      case WintSuperkollAPIModelQuestionsAndAnswers.AnswerKind.SingleLineText:
        return answer.textValue;
      case WintSuperkollAPIModelQuestionsAndAnswers.AnswerKind.Numeric:
        return answer.numericValue;
      case WintSuperkollAPIModelQuestionsAndAnswers.AnswerKind.Selection:
        return answer.selectionValue;
      case WintSuperkollAPIModelQuestionsAndAnswers.AnswerKind.StringSelection:
        return answer.stringSelectionValue;
      case WintSuperkollAPIModelQuestionsAndAnswers.AnswerKind.File:
        return answer.fileValue?.map(
          (f): UploadedFile => ({
            id: f.fileId,
            fileName: f.fileName ?? "",
            contentType: f.mediaType ?? "",
            data: f.fileContent ?? "",
          })
        );
      case WintSuperkollAPIModelQuestionsAndAnswers.AnswerKind.Matrix:
        return getMatrix(question, answer);
    }
  };

  const getMatrixValue = (
    values: WintSuperkollAPIModelQuestionsAndAnswers.MatrixValueModel,
    valueKind: WintSuperkollAPIModelQuestionsAndAnswers.QuestionMatrixValueKind
  ) => {
    switch (valueKind) {
      case WintSuperkollAPIModelQuestionsAndAnswers.QuestionMatrixValueKind.Boolean:
        return values.booleanValue;
      case WintSuperkollAPIModelQuestionsAndAnswers.QuestionMatrixValueKind.Text:
        return values.textValue;
      case WintSuperkollAPIModelQuestionsAndAnswers.QuestionMatrixValueKind.Numeric:
        return values.numericValue;
    }
  };

  const getMatrix = (question: Question, answer?: Answer) => {
    const innerFields: Record<any, any>[] = [];

    const rowCount = question.matrixDefinition?.rowCount;
    if (rowCount) {
      for (let i = 0; i < rowCount; i++) {
        const innerField: Record<any, any> = {};
        question.matrixDefinition?.questionMatrixColumnDefinitions?.forEach((m, index) => {
          innerField[`${m.ordinal}`] = undefined;
        });
        innerFields[i] = innerField;
      }
    }

    let rowIndex = -1;
    let currentRowOrdinal: number | undefined = undefined;
    answer?.matrixValue
      ?.sort((a, b) => a.rowOrdinal - b.rowOrdinal)
      .forEach((m) => {
        if (currentRowOrdinal !== m.rowOrdinal) {
          currentRowOrdinal = m.rowOrdinal;
          rowIndex += 1;
        }

        const matrixColumn = question.matrixDefinition?.questionMatrixColumnDefinitions?.find(
          (q) => q.ordinal === m.columnOrdinal
        );

        if (matrixColumn) {
          if (innerFields[rowIndex] === undefined) {
            innerFields[rowIndex] = { uniqueId: rowIndex };
          }
          innerFields[rowIndex][`${m.columnOrdinal}`] = getMatrixValue(m, matrixColumn.valueKind);
        }
      });

    return innerFields;
  };

  const getInitialValues = () => {
    const values: Record<string, any> = {};

    questionGroups.forEach((qg) => {
      if (qg.isOptional) {
        const isGroupEnabled = groupAnswers?.find((a) => a.questionGroupId === qg.id)?.isEnabled;
        values[FormHelper.getDynamicFormFieldName(qg.id!)] =
          isGroupEnabled === undefined ? undefined : isGroupEnabled ? 1 : 0;
      }
      qg.questions?.forEach((q) => {
        const answer = answers.find((a) => a.questionId === q.id);
        values[FormHelper.getDynamicFormFieldName(q.id!)] =
          answer !== undefined
            ? getValue(answer, q)
            : q.answerKind === WintSuperkollAPIModelQuestionsAndAnswers.AnswerKind.Matrix
            ? getMatrix(q)
            : undefined;
      });
    });

    return values;
  };

  return { getInitialValues };
}

export default useDynamicFormInitialValues;
